import React, { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Header, SubModal } from "./partials";
import NavRail from "./partials/NavRail";
import NavPanel from "./partials/NavPanel";
import { activeChildKey, isModuleActive, toPath } from "./navPaths";
import { SecondaryNavProvider, useSecondaryNav } from "./SecondaryNavContext";
import styles from "./styles.module.css";
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  Layout,
  List,
  Popover,
  Tag,
  Tooltip,
  message,
  notification,
} from "antd";
import {
  useMediaQuery,
  useModules,
  useTheme,
  useTicketUnread,
  useTimeTracking,
  useTrackingSync,
} from "../hooks";
import { Moon, Sun } from "react-feather";
import { Footer } from "antd/es/layout/layout";
import {
  UserOutlined,
  TeamOutlined,
  ClusterOutlined,
  BellOutlined,
  AppstoreOutlined,
  LogoutOutlined,
  HistoryOutlined,
  DashboardOutlined,
  LoadingOutlined,
  ApartmentOutlined,
  CustomerServiceOutlined,
  IdcardOutlined,
  CrownOutlined,
  EyeOutlined,
  CaretDownOutlined,
  OrderedListOutlined,
  UserSwitchOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  ClockCircleFilled,
  ReloadOutlined,
  QuestionCircleOutlined,
  PushpinFilled,
  PushpinOutlined,
  SearchOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { actions as authActions } from "../store/authRedux/actions";
import { AuthApi, UserApi } from "../services";
import chatSocket, * as chatSocketModule from "../utils/chatSocket";
import { playNotifSound } from "../utils/notifSound";
import {} from "antd";
import { helpers } from "../utils";
import { APP_VERSION } from "../constants";
import { actions as AuthActions } from "../store/authRedux/actions";
import { actions as CommonActions } from "../store/commonRedux/actions";
import { tourFlagForPath } from "../utils/tourFlags";
import {
  requestFcmToken,
  isNotificationSupported,
  getNotificationPermission,
} from "../utils/Messaging";
import {
  formatTime,
  getPermissionedMenuItems,
  hasPermission,
} from "../utils/helpers";
import { SampleAvatar } from "../assets/images";
import { ChevronDown } from "react-feather";
import RegisterOrgModal from "./partials/RegisterOrg";
import BottomNavigation from "./partials/BottomNavigation";
import {
  CountUpTimer,
  FloatChat,
  InstallAppPrompt,
  CommandPalette,
  useCommandPalette,
  NotifPermissionModal,
  UserAvatar,
  WorkspaceWelcome,
} from "../components";
import {
  openNotificationAction,
  switchOrganization,
  takeOrgSwitchNotice,
} from "../utils/orgSwitch";
import VerifyActivityModal from "../pages/Projects/screens/Edit/partials/Kanban/singleTask/partials/verifyActivityModal";

const { Content } = Layout;

const notifPlacement = () =>
  window.matchMedia("(max-width: 992px)").matches ? "top" : "bottomLeft";

const NOTIF_PROMPT_MAX = 3;
const NOTIF_PROMPT_GAP_DAYS = 3;
/* Asking for notifications on the way in reads as a toll gate, so the prompt
 * waits until the user has been in the system for five minutes. The wait is
 * measured from a persisted stamp, not from mount, so reloading or moving
 * between pages doesn't restart it. */
const NOTIF_PROMPT_MIN_AGE_MS = 5 * 60 * 1000;
/* On a later session those five minutes are long gone; this keeps the modal
 * from landing in the same frame as the page. */
const NOTIF_PROMPT_SETTLE_MS = 12000;
const NOTIF_PROMPT_RETRY_MS = 2000;
const NOTIF_PROMPT_MAX_WAIT_MS = 60000;

/* A walkthrough owns the screen while it runs — the tour mask sits above the
 * modal layer, so opening on top of it would leave a modal the user can see and
 * can't reach. Someone else's modal is the same story, minus the mask. */
const isTourOpen = () => Boolean(document.querySelector(".ant-tour"));

const isOtherModalOpen = () =>
  Boolean(
    document.querySelector(".ant-modal-wrap:not([style*='display: none'])"),
  );

function getItem(label, key, icon, children, type) {
  return {
    key,
    icon,
    children,
    label,
    type,
  };
}

const LayoutPage = ({ children }) => {
  const { activeTracking, setTracking } = useTimeTracking();
  const user = useSelector(({ auth }) => auth.user);
  const organization = useSelector(({ auth }) => auth.organization);
  const fcm_token = useSelector(({ auth }) => auth.fcm_token);
  const notifRequested = useSelector(({ common }) => common.notifRequested);
  const notifPromptCount = useSelector(({ common }) => common.notifPromptCount);
  const notifFirstSeenAt = useSelector(({ common }) => common.notifFirstSeenAt);
  const allOrganizations = useSelector(({ auth }) => auth.allOrganizations);
  /* گذاشته‌ی تعویض سازمان، که با همان بارگذاری مجدد به اینجا می‌رسد. */
  const orgSwitchNotice = useSelector(({ common }) => common.orgSwitchNotice);
  /* همان فهرست عضویت‌های مشتری که هدر هم نشان می‌دهد؛ اینجا نسخه‌ی موبایل است. */
  const customerOf = useSelector(({ auth }) => auth.customerOf);
  const portalCustomer = useSelector(({ auth }) => auth.portalCustomer);

  const { isAuthorized } = useSelector(
    ({ auth }) => ({ isAuthorized: auth.user != null }),
    shallowEqual,
  );
  const isMobile = useMediaQuery("(max-width: 992px)");
  const { isDark, toggle: toggleTheme } = useTheme();
  const { visible: modules, has: hasModule } = useModules();
  const canManageSubscription =
    helpers.IsUerOwner() || hasPermission("manage_org");

  /* Mounted here so Ctrl/Cmd+K works on every screen, not only the ones that
   * happen to have a search box. */
  const palette = useCommandPalette();

  // Mounted once for the whole app: the running timer is shared state, and this
  // is what keeps it in step with timers started or stopped elsewhere.
  useTrackingSync(isAuthorized && !portalCustomer && hasModule("time_tracking"));

  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const tourFlag = tourFlagForPath(pathname);

  /* A replay belongs to the page it was started from. Leaving the page ends it,
   * so a stale flag can't make an unrelated screen open its tour. */
  const replayPath = useRef(pathname);
  useEffect(() => {
    if (replayPath.current === pathname) return;
    replayPath.current = pathname;
    dispatch(CommonActions.setCustomOnboarding({ tourReplay: null }));
  }, [pathname, dispatch]);

  /* «به سازمان فلان وارد شدی».
   *
   * تعویض سازمان از هر جایی که شروع شود — سوییچر هدر، صفحه‌ی سازمان، کلیک روی
   * نوتیفیکیشنی که به سازمان دیگری تعلق دارد — کل اپ را دوباره بار می‌کند. این
   * تنها چیزی است که بعدش می‌گوید کجاییم؛ یک بار نشان داده می‌شود و پاک. */
  useEffect(() => {
    if (!orgSwitchNotice) return;

    const notice = takeOrgSwitchNotice(orgSwitchNotice);
    dispatch(CommonActions.setVariables({ orgSwitchNotice: null }));
    if (!notice) return;

    notification.success({
      key: "org-switch",
      className: "ds-notif",
      message: "خوش اومدی!",
      description: `وارد سازمان «${notice.title}» شدی.`,
      icon: <ApartmentOutlined />,
      placement: notifPlacement(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSwitchNotice]);

  useEffect(() => {
    if (!user?.id) return;
    chatSocket
      .private(`users.${user.id}`)
      .listen(".new_chat_message", (data) => {
        const msg = data?.chatMessage;
        const roomId = msg?.chat_room_id;
        const senderName = msg?.sender?.full_name || "Someone";
        const text = msg?.message || "New message";

        if (msg?.sender_id === user.id || msg?.sender?.id === user.id) return;

        if (chatSocketModule.activeRoomId === roomId) return;

        queryClient.setQueryData(["[List] ChatRooms"], (old) => {
          if (!old) return old;
          return old.map((room) =>
            room.id === roomId
              ? {
                  ...room,
                  last_message: msg,
                  unread_count: (room.unread_count || 0) + 1,
                }
              : room,
          );
        });

        playNotifSound();

        notification.info({
          className: "ds-notif",
          message: senderName,
          description: text,
          placement: notifPlacement(),
        });
      })
      .notification((payload) => {
        if (payload?.id) showIncomingNotification(payload.id);
      });
    return () => {
      chatSocket.leave(`users.${user.id}`);
    };
  }, [user?.id]);

  function showAppNotification({ key, title, description, onClick }) {
    playNotifSound();

    notification.open({
      key,
      className: "ds-notif",
      message: title || "اعلان جدید",
      description: description || "",
      icon: <BellOutlined />,
      placement: notifPlacement(),
      style: { cursor: onClick ? "pointer" : "default" },
      onClick,
    });
  }

  async function showIncomingNotification(id) {
    queryClient.invalidateQueries({ queryKey: ["[DropDown] Notifications"] });
    queryClient.invalidateQueries({ queryKey: ["[List] Notifications"] });

    let item = null;
    try {
      const res = await AuthApi.getNotifications({ params: { per_page: 10 } });
      item = res?.data?.notifications?.find((n) => n.id === id) || null;
    } catch {}

    const key = `notif-${id}`;
    const action = item?.data?.action;

    showAppNotification({
      key,
      title: item?.data?.title,
      description: item?.translated_description,
      onClick: () => {
        notification.destroy(key);
        readNotification.mutate({ params: { id } });
        if (action) openNotificationAction(item, navigate);
      },
    });
  }

  const [notifOpen, setNotifOpen] = useState(false);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [readLoading, setReadLoading] = useState(0);
  const [verifyModal, setVerifyModal] = useState({ show: false, data: null });
  const [notifPermOpen, setNotifPermOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);

  const fcmMutate = useMutation(UserApi.updateMyProfile, {
    onSuccess: (res) => {},
    onError: (error) => {},
  });

  const readNotification = useMutation(AuthApi.readNotification, {
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ["[List] Notifications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["[DropDown] Notifications"],
      });
      setReadLoading(0);
    },
    onError: (error) => {
      setReadLoading(0);
    },
  });

  const readAllNotification = useMutation(AuthApi.readAllNotification, {
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ["[List] Notifications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["[DropDown] Notifications"],
      });
      setReadLoading(0);
    },
    onError: (error) => {},
  });

  const logoutMutate = useMutation(AuthApi.logoutUser, {
    onSuccess: (res) => {
      message.success({ content: "با موفقیت خارج شدید" });
      dispatch(authActions.logout());
    },
    onError: (error) => {
      if (error.response.status == 400) {
        message.error({ content: "خطا در ثبت داده" });
      }
    },
  });

  /* هر بار که اپ بالا می‌آید، فهرست فضاهای کاری تازه می‌شود.
   *
   * تا حالا `allOrganizations` و `customerOf` فقط لحظه‌ی ورود پر می‌شدند و در
   * redux می‌ماندند؛ پس عوض‌شدن لوگو یا نام سازمان، یا گرفتن و پس‌گرفتن دسترسی
   * پرتال، تا خروج و ورود دوباره دیده نمی‌شد. */
  useQuery({
    queryKey: ["[Boot] Workspaces"],
    queryFn: () => AuthApi.getProfile(),
    enabled: isAuthorized,
    staleTime: 0,
    onSuccess: (res) => {
      const freshOrganizations = res?.data?.user?.organizations || [];
      const memberships = res?.data?.customer_of || [];

      dispatch(authActions.setAllOrganizations(freshOrganizations));
      dispatch(authActions.setCustomerOf(memberships));

      /* سازمان جاری هم همان شیء تازه می‌شود، وگرنه هدر لوگوی قدیمی را نگه می‌دارد. */
      const currentUuid = organization?.uuid;
      const fresh =
        freshOrganizations.find((item) => item.uuid === currentUuid) ||
        memberships.find((item) => item.organization?.uuid === currentUuid)?.organization;

      if (fresh) dispatch(authActions.setOrganization(fresh));
    },
  });

  const { data: notifications } = useQuery({
    /* اعلان‌ها اندپوینت پنل‌اند و برای نماینده‌ی مشتری ۴۰۳ می‌دهند. */
    enabled: !portalCustomer,
    queryKey: ["[DropDown] Notifications"],
    queryFn: () =>
      AuthApi.getNotifications({
        params: {
          per_page: 5,
        },
      }).then((result) => {
        return result.data.notifications;
      }),
    initialData: [],
    cacheTime: 0,
  });

  /* تیکت‌های پشتیبانی مالِ سازمان‌اند؛ نماینده‌ی مشتری این اندپوینت را ندارد. */
  const ticketUnread = useTicketUnread(Boolean(user?.id) && !portalCustomer);

  /* The sidebar is built by `getPermissionedMenuItems`, which reads the enabled
   * modules off the organization. Passing them in is what makes the menu
   * rebuild the moment they change — without it a module toggled in settings
   * only appears after the next navigation. */
  /* The sidebar used to list the first twenty projects the API happened to
   * return, in that order, with project twenty-one simply absent. It shows this
   * person's own shortcuts instead — what they pinned and what they last opened
   * — both held in the browser, with a link to the full list for everything
   * else. Nothing is fetched for it: each entry carries its own title and
   * image, so a pinned project appears whether or not it is on the first page
   * of the projects list. */
  const pinnedProjects = useSelector(
    ({ common, auth }) => common.projectPins?.[auth.organization?.uuid] || [],
  );
  const recentProjects = useSelector(
    ({ common, auth }) => common.projectRecents?.[auth.organization?.uuid] || [],
  );

  const rawItems = useMemo(() => {
    /* پرتال مشتری همین چیدمان و همین سایدبار را دارد، فقط منوی دیگری: مشتری
       نه پروژه می‌سازد، نه تسک دارد، نه به تنظیمات سازمان دسترسی دارد. */
    if (portalCustomer) {
      return [
        {
          key: "/portal/dashboard",
          icon: <DashboardOutlined />,
          label: "داشبورد",
        },
        {
          key: "/portal/projects",
          icon: <ApartmentOutlined />,
          label: "پروژه‌ها",
        },
        {
          key: "/portal/tickets",
          icon: <CustomerServiceOutlined />,
          label: "درخواست‌های من",
        },
      ];
    }

    const projectEntry = (project, { pinned: isPinned } = {}) => ({
      key: `/projects/edit/${project.uuid}`,
      icon: (
        <Avatar
          size={18}
          shape="square"
          src={project.image}
          icon={<AppstoreOutlined />}
          style={{ fontSize: 10 }}
        />
      ),
      label: (
        <span className={styles.projectEntry}>
          <span className={styles.projectEntryName}>{project.title}</span>
          {isPinned && (
            /* Unpinning belongs where the pin is visible. Sending someone to the
             * projects page to undo a shortcut they made from the sidebar is a
             * round trip for a one-click action. */
            <Tooltip title="برداشتن سنجاق">
              <span
                role="button"
                tabIndex={-1}
                className={styles.projectEntryUnpin}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  dispatch(
                    CommonActions.toggleProjectPin(organization?.uuid, project),
                  );
                }}
              >
                <PushpinFilled />
              </span>
            </Tooltip>
          )}
        </span>
      ),
    });

    const pinned = pinnedProjects;
    /* A project already pinned is not also "recent": listed twice it is a
     * longer menu that says less. */
    const recent = recentProjects.filter(
      (project) => !pinned.some((item) => item.uuid === project.uuid),
    );

    const groups = [
      /* The heading stays whether or not anything is under it, with a line
       * explaining what the empty space is for. A group that only appears once
       * you have already used the feature cannot teach you the feature. */
      { type: "group", key: "pinned-group", label: "سنجاق‌شده" },
      ...(pinned.length
        ? pinned.map((project) => projectEntry(project, { pinned: true }))
        : [
            {
              key: "pinned-empty",
              disabled: true,
              icon: <PushpinOutlined />,
              label: (
                <span className={styles.projectEntryHint}>
            چیزی سنجاق نشده
                </span>
              ),
            },
          ]),
      ...(recent.length
        ? [
            { type: "group", key: "recent-group", label: "اخیر" },
            ...recent.map((project) => projectEntry(project)),
          ]
        : []),
    ];

    return getPermissionedMenuItems(
      getItem("پروژه ها", "projects", <AppstoreOutlined />, [
        {
          label: "همه پروژه‌ها",
          key: "projects",
          icon: <OrderedListOutlined />,
        },
        {
          label: "پروژه جدید",
          key: "/projects?action=new",
          icon: <PlusOutlined />,
        },
        ...groups,
      ]),
      modules,
    );
  }, [pinnedProjects, recentProjects, organization?.uuid, modules, dispatch, portalCustomer]);

  /* Support gets the same unread treatment as chat: a count on the menu entry
   * so a reply is noticeable without opening the section. */
  const items = useMemo(() => {
    if (!ticketUnread) return rawItems;

    return rawItems.map((item) =>
      item?.key === "tickets"
        ? {
            ...item,
            label: (
              <span className="d-flex align-items-center justify-content-between gap-2 w-100">
                <span>{item.label}</span>
                <Badge count={ticketUnread} size="small" />
              </span>
            ),
          }
        : item,
    );
  }, [rawItems, ticketUnread]);

  const { nav: pageNav } = useSecondaryNav();

  const activeModule = useMemo(
    () => items.find((item) => item && isModuleActive(item, pathname)),
    [items, pathname],
  );

  const [openModuleKey, setOpenModuleKey] = useState(activeModule?.key);

  useEffect(() => {
    if (activeModule?.key) setOpenModuleKey(activeModule.key);
  }, [pathname, activeModule?.key]);

  const panel = useMemo(() => {
    const openItem = items.find((item) => item && item.key === openModuleKey);
    const moduleChildren = openItem?.children ?? [];

    const isPeekingElsewhere =
      openModuleKey && activeModule?.key && openModuleKey !== activeModule.key;

    if (!isPeekingElsewhere && pageNav?.items?.length) return pageNav;

    if (moduleChildren.length) {
      return {
        title: typeof openItem?.label === "string" ? openItem.label : undefined,
        items: moduleChildren,
        activeKey: activeChildKey(moduleChildren, pathname),
        onSelect: (key) => navigate(toPath(key)),
      };
    }

    return pageNav?.items?.length ? pageNav : { items: [] };
  }, [pageNav, items, openModuleKey, activeModule?.key, pathname, navigate]);

  const organizations = Array.isArray(allOrganizations) ? allOrganizations : [];
  const organizationLimitReached = organizations.length >= 3;

  const profileItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "پروفایل من",
    },
    {
      key: "changelog",
      icon: <HistoryOutlined />,
      label: "تغییرات نسخه‌ها",
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "خروج از حساب",
      danger: true,
      disabled: logoutMutate.isLoading,
    },
  ];

  const handleProfileMenuClick = ({ key }) => {
    if (key === "profile") navigate("/profile");
    if (key === "changelog") navigate("/changelog");
    if (key === "logout") logoutMutate.mutate();
  };

  const organizationItems = [
    ...organizations.map((org) => ({
      key: `organization:${org.uuid}`,
      label: (
        <span className={styles.workspaceOption}>
          <Avatar
            size={28}
            src={org.avatar?.original_url}
            icon={<ApartmentOutlined />}
          />
          <span className={styles.workspaceOptionText}>
            <strong>{org?.title}</strong>
            {org.uuid === organization?.uuid && <small>سازمان فعلی</small>}
          </span>
        </span>
      ),
    })),
    ...(customerOf?.length
      ? [
          { type: "divider" },
          ...customerOf.map((membership) => ({
            key: `portal:${membership.customer_id}`,
            label: (
              <span className={styles.workspaceOption}>
                <Avatar size={28} icon={<IdcardOutlined />} />
                <span className={styles.workspaceOptionText}>
                  <strong>{membership.organization?.title}</strong>
                  <small>پرتال مشتری</small>
                </span>
              </span>
            ),
          })),
        ]
      : []),
    { type: "divider" },
    {
      key: "new-organization",
      icon: <PlusOutlined />,
      disabled: organizationLimitReached,
      label: organizationLimitReached
        ? "حداکثر ۳ سازمان"
        : "افزودن سازمان جدید",
    },
  ];

  const handleOrganizationMenuClick = ({ key }) => {
    if (key === "new-organization") {
      if (!organizationLimitReached) setCreateOrgOpen(true);
      return;
    }
    if (key.startsWith("portal:")) {
      const membership = customerOf?.find(
        (item) => String(item.customer_id) === key.replace("portal:", ""),
      );
      if (membership) {
        queryClient.clear();
        dispatch(authActions.setOrganization(membership.organization));
        dispatch(authActions.setPortalCustomer(membership));
      }
      return;
    }
    const uuid = key.replace("organization:", "");
    const target = organizations.find((org) => org.uuid === uuid);
    if (target && target.uuid !== organization?.uuid) {
      switchOrganization(target, queryClient, dispatch);
    }
  };

  const getRemainDate = (date) => {
    return Math.floor((new Date(date) - new Date()) / 86400000);
  };

  /* A plan with no expiry date has no day count to show — the badge just says
   * it is active, instead of printing a number derived from nothing. */
  const planRemainDays = organization?.plan_expire_at
    ? Math.max(getRemainDate(organization.plan_expire_at), 0)
    : null;

  async function syncFcmToken() {
    const token = await requestFcmToken();
    if (!token) {
      console.error("[FCM] ✗ no token — profile patch skipped");
      return false;
    }

    try {
      await fcmMutate.mutateAsync({ body: { fcm_token: token } });
      dispatch(AuthActions.setFcmToken(token));
      console.log("[FCM] ✓ fcm_token saved on profile");
      return true;
    } catch (err) {
      console.error("[FCM] ✗ profile patch failed", {
        status: err?.response?.status,
        data: err?.response?.data,
        error: err,
      });
      return false;
    }
  }

  const rememberNotifPrompt = () =>
    dispatch(
      CommonActions.setVariables({
        notifRequested: new Date(),
        notifPromptCount: (notifPromptCount || 0) + 1,
      }),
    );

  const acceptNotifPrompt = async () => {
    setNotifLoading(true);
    await syncFcmToken();
    setNotifLoading(false);
    setNotifPermOpen(false);
    rememberNotifPrompt();
  };

  const declineNotifPrompt = () => {
    setNotifPermOpen(false);
    rememberNotifPrompt();
  };


  useEffect(() => {
    if (!isAuthorized || fcm_token) return;
    if (getNotificationPermission() !== "granted") return;

    syncFcmToken();
  }, [isAuthorized, fcm_token]);

  /* Stamped once, the first time the user reaches an authorised screen. */
  useEffect(() => {
    if (!isAuthorized || notifFirstSeenAt) return;
    dispatch(CommonActions.setVariables({ notifFirstSeenAt: new Date() }));
  }, [isAuthorized, notifFirstSeenAt, dispatch]);

  useEffect(() => {
    if (!isAuthorized || fcm_token) return;
    if (!isNotificationSupported()) return;
    if (getNotificationPermission() !== "default") return;
    if ((notifPromptCount || 0) >= NOTIF_PROMPT_MAX) return;
    if (
      notifRequested &&
      !helpers.isDateGreater(notifRequested, new Date(), NOTIF_PROMPT_GAP_DAYS)
    ) {
      return undefined;
    }

    // set by the effect above; this one runs again on the render that follows
    const firstSeen = notifFirstSeenAt && new Date(notifFirstSeenAt).getTime();
    if (!firstSeen || Number.isNaN(firstSeen)) return undefined;

    const delay = Math.max(
      NOTIF_PROMPT_MIN_AGE_MS - (Date.now() - firstSeen),
      NOTIF_PROMPT_SETTLE_MS,
    );

    let timer;
    let waited = 0;

    const attempt = () => {
      /* A background tab isn't a conflict, it's just nobody looking — keep
       * waiting instead of burning the budget, so the prompt is still there
       * when the user comes back. */
      if (document.visibilityState !== "visible") {
        timer = setTimeout(attempt, NOTIF_PROMPT_RETRY_MS);
        return;
      }

      if (isTourOpen() || isOtherModalOpen()) {
        waited += NOTIF_PROMPT_RETRY_MS;
        // long tours get the screen to themselves; we try again next session
        if (waited >= NOTIF_PROMPT_MAX_WAIT_MS) return;
        timer = setTimeout(attempt, NOTIF_PROMPT_RETRY_MS);
        return;
      }

      setNotifPermOpen(true);
    };

    timer = setTimeout(attempt, delay);

    return () => clearTimeout(timer);
  }, [
    isAuthorized,
    fcm_token,
    notifRequested,
    notifPromptCount,
    notifFirstSeenAt,
  ]);

  return (
    <>
      <Layout

        hasSider
        className="w-100 h-100"
        style={{ "--rail-accent": helpers.getAccentColor(organization) }}
      >
        <RegisterOrgModal isOpen={createOrgOpen} setIsOpen={setCreateOrgOpen} />
        {!portalCustomer && (
          <CommandPalette open={palette.open} onClose={palette.close} />
        )}
        {/* درخواست اجازه‌ی اعلان برای کسی که اعلانی دریافت نمی‌کند بی‌معنی است. */}
        {!portalCustomer && (
        <NotifPermissionModal
          isOpen={notifPermOpen}
          loading={notifLoading}
          onAccept={acceptNotifPrompt}
          onDecline={declineNotifPrompt}
        />
        )}
        {!portalCustomer && hasModule("time_tracking") && (
          <VerifyActivityModal
            isOpen={verifyModal.show}
            setIsOpen={(state) => setVerifyModal({ show: state, data: null })}
            defaultValue={verifyModal?.data}
            taskId={activeTracking?.task_id}
            onFinish={(res) => {
              queryClient.invalidateQueries({
                queryKey: [
                  `[Single-${res?.task_id ?? activeTracking?.task_id}] Task`,
                ],
              });
              setTracking(res);
              message.success({ content: "فعالیت با موفقیت ثبت شد" });
            }}
          />
        )}
        <SubModal />
        {!isMobile ? (
          <>
            <NavRail
              items={items}
              pathname={pathname}
              openKey={openModuleKey}
              onOpen={setOpenModuleKey}
              onNavigate={(key) => navigate(toPath(key))}
              onLocked={(item) =>
                dispatch(
                  CommonActions.setSubModal({
                    feature: item.key,
                    title: item.label,
                  }),
                )
              }
              onLogout={() => logoutMutate.mutate()}
              logoutLoading={logoutMutate.isLoading}
            />
            <NavPanel
              title={panel.title}
              titleExtra={panel.titleExtra}
              header={panel.header}
              meta={panel.meta}
              items={panel.items}
              activeKey={panel.activeKey}
              onSelect={panel.onSelect}
              onLocked={(item) =>
                dispatch(
                  CommonActions.setSubModal({
                    feature: item.key,
                    title: item.label,
                  }),
                )
              }
            />
          </>
        ) : (
          <Header />
        )}
        <Layout>
          {!isMobile && (
            <div className="ds-topbar">
              {}
              <div className="d-flex align-items-center gap-3">
                <Dropdown
                  menu={{
                    items: organizationItems,
                    onClick: handleOrganizationMenuClick,
                  }}
                  trigger={["click"]}
                  placement="bottomRight"
                  overlayClassName={styles.accountDropdown}
                >
                  <button type="button" className={styles.organizationTrigger}>
                    {organization?.avatar && (
                      <Avatar
                        size={22}
                        src={organization.avatar?.original_url}
                        icon={<ApartmentOutlined />}
                      />
                    )}
                    <span className={styles.orgName}>
                      {organization?.title}
                    </span>
                    <ChevronDown
                      style={{ width: 14, color: "var(--ds-text-muted)" }}
                    />
                  </button>
                </Dropdown>

                {/* اشتراک مالِ سازمان است، نه مشتریِ آن. */}
                {!portalCustomer && canManageSubscription && (
                  <Tooltip
                    title={
                      organization?.hasPlan ? "مدیریت اشتراک" : "خرید اشتراک"
                    }
                  >
                    <Link
                      to="/subscriptions"
                      className={styles.subBadge}
                      aria-label={
                        organization?.hasPlan ? "مدیریت اشتراک" : "خرید اشتراک"
                      }
                    >
                      {organization?.hasPlan ? (
                        <Tag
                          color="success"
                          bordered={false}
                          className="d-inline-flex align-items-center gap-1"
                          style={{ margin: 0, padding: "4px 10px" }}
                        >
                          <CrownOutlined />
                          <span>
                            اشتراک فعال
                            {planRemainDays !== null
                              ? ` · ${planRemainDays} روز`
                              : ""}
                          </span>
                          <LeftOutlined className={styles.subBadgeArrow} />
                        </Tag>
                      ) : (
                        <Tag
                          color="warning"
                          bordered={false}
                          className="d-inline-flex align-items-center gap-1"
                          style={{ margin: 0, padding: "4px 10px" }}
                        >
                          <CrownOutlined />
                          <span>بدون اشتراک</span>
                          <LeftOutlined className={styles.subBadgeArrow} />
                        </Tag>
                      )}
                    </Link>
                  </Tooltip>
                )}
              </div>

              {}
              <div className="d-flex align-items-center gap-3">
                {}
                {/* A visible way in, because a shortcut nobody is told about is
                    a shortcut nobody uses. */}
                <Tooltip title="جست‌وجو در همه‌جا">
                  <button
                    type="button"
                    className={styles.paletteTrigger}
                    onClick={palette.toggle}
                  >
                    <SearchOutlined />
                    {!isMobile && (
                      <>
                        <span className={styles.paletteTriggerLabel}>
                          جست‌وجو…
                        </span>
                        <span className={styles.paletteTriggerKeys}>
                          <span>⌘</span>
                          <span>K</span>
                        </span>
                      </>
                    )}
                  </button>
                </Tooltip>

                {tourFlag && (
                  <Button
                    type="text"
                    shape="circle"
                    onClick={() =>
                      dispatch(
                        CommonActions.setCustomOnboarding({
                          tourReplay: tourFlag,
                          tourReplayNonce: Date.now(),
                        }),
                      )
                    }
                    icon={
                      <QuestionCircleOutlined
                        style={{
                          fontSize: 16,
                          color: "var(--ds-text-secondary)",
                        }}
                      />
                    }
                    style={{ width: 36, height: 36 }}
                    data-tour="page-help"
                    title="راهنمای این صفحه"
                  />
                )}
                {}
                <Button
                  type="text"
                  shape="circle"
                  onClick={toggleTheme}
                  icon={
                    isDark ? (
                      <Sun
                        style={{ width: 16, color: "var(--ds-text-secondary)" }}
                      />
                    ) : (
                      <Moon
                        style={{ width: 16, color: "var(--ds-text-secondary)" }}
                      />
                    )
                  }
                  style={{ width: 36, height: 36 }}
                  title={isDark ? "حالت روشن" : "حالت تاریک"}
                />

                {!portalCustomer && hasModule("time_tracking") && activeTracking && (
                  <div
                    className={`d-flex align-items-center gap-1 ${styles.trackingChip}`}
                    onClick={() =>
                      setVerifyModal({
                        show: true,
                        data: { ...activeTracking, to: new Date() },
                      })
                    }
                  >
                    <ClockCircleFilled />
                    <CountUpTimer
                      startDate={new Date(activeTracking.from)}
                      color="white"
                      fontSize={12}
                    />
                  </div>
                )}

                {/* اعلان‌ها روی اندپوینت پنل‌اند و برای نماینده‌ی مشتری معنایی ندارند. */}
                {!portalCustomer && (
                <Popover
                  content={
                    <div style={{ width: 320 }}>
                      <div
                        className="d-flex justify-content-between align-items-center"
                        style={{ marginBottom: 12 }}
                      >
                        <span style={{ fontWeight: 600, fontSize: 14 }}>
                          اعلان‌ها
                        </span>
                        <Button
                          type="text"
                          size="small"
                          loading={readAllNotification.isLoading}
                          onClick={() =>
                            readAllNotification.mutate({ params: {} })
                          }
                          icon={<EyeOutlined />}
                          style={{
                            color: "var(--ds-text-secondary)",
                            fontSize: 12,
                          }}
                        >
                          خواندن همه
                        </Button>
                      </div>
                      <div className={styles.notifList}>
                        {notifications?.length === 0 && (
                          <div className={styles.notifEmpty}>
                            اعلان جدیدی ندارید
                          </div>
                        )}
                        {notifications?.map((item) => (
                          <div
                            key={item.id}
                            className={styles.notifItem}
                            style={{
                              background: item.read_at
                                ? "transparent"
                                : "var(--notif-unread-bg)",
                              border: `1px solid ${
                                item.read_at
                                  ? "transparent"
                                  : "var(--notif-unread-border)"
                              }`,
                            }}
                          >
                            <span className={styles.notifItemText}>
                              {item.translated_description}
                            </span>
                            <Button
                              type="text"
                              size="small"
                              icon={<EyeOutlined />}
                              loading={readLoading === item.id}
                              style={{
                                flexShrink: 0,
                                color: "var(--notif-unread-dot)",
                              }}
                              onClick={async () => {
                                setReadLoading(item.id);
                                await readNotification.mutateAsync({
                                  params: { id: item.id },
                                });
                                openNotificationAction(item, navigate);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      <div className={styles.notifFooter}>
                        <Button
                          type="link"
                          size="small"
                          href="/notifications"
                          style={{ fontSize: 12 }}
                        >
                          مشاهده همه اعلان‌ها
                        </Button>
                      </div>
                    </div>
                  }
                  trigger="click"
                  open={notifOpen}
                  onOpenChange={setNotifOpen}
                  arrow={false}
                >
                  <Badge
                    count={notifications?.filter((n) => !n.read_at).length}
                    size="small"
                    offset={[-2, 2]}
                  >
                    <Button
                      type="text"
                      shape="circle"
                      icon={<BellOutlined style={{ fontSize: 17 }} />}
                      className={styles.notifIconBtn}
                    />
                  </Badge>
                </Popover>
                )}

                <Dropdown
                  menu={{ items: profileItems, onClick: handleProfileMenuClick }}
                  trigger={["click"]}
                  placement="bottomLeft"
                  overlayClassName={styles.accountDropdown}
                  arrow={false}
                >
                  <button type="button" className={styles.userDropdownTrigger}>
                    {logoutMutate.isLoading ? (
                      <LoadingOutlined />
                    ) : (
                      /* antd's Badge offsets from the square bounding box, and
                         an offset that reaches the bottom edge lands the dot in
                         the empty corner outside a round avatar. Sizing it from
                         the avatar's own diameter seats it on the rim. */
                      <span
                        className={styles.presence}
                        style={{ "--avatar-size": "32px" }}
                      >
                        <UserAvatar user={user} size={32} />
                        <span className={styles.presenceDot} aria-hidden="true" />
                      </span>
                    )}
                    <span className={styles.userDisplayName}>
                      {`${user?.first_name || ""} ${user?.last_name || ""}`.trim()}
                    </span>
                    <CaretDownOutlined
                      style={{ fontSize: 10, color: "var(--ds-text-muted)" }}
                    />
                  </button>
                </Dropdown>
              </div>
            </div>
          )}
          <Content
            className="overflow-scroll d-flex flex-column"
            style={{
              background: "var(--ds-bg)",
              /* Room for the bottom bar, plus the home indicator under it, so
                 the last row of a list is reachable rather than parked behind
                 the navigation. */
              paddingBottom: isMobile
                ? "calc(72px + env(safe-area-inset-bottom))"
                : undefined,
            }}
          >
            <div className={`${styles.content} flex-grow-1 ds-shell-content`}>
              {children}
            </div>
            {}
            {(!/\/(create|edit)(\/|$)/.test(pathname) ||
              /^\/projects\/edit\//.test(pathname)) && (
              <Footer className={styles.footer}>
                ساخته شده با ❤️ · © {new Date().getFullYear()} سولوتک ·{" "}
                <Link to="/changelog" className={styles.footerVersion}>
                  نسخه {APP_VERSION}
                </Link>
              </Footer>
            )}
          </Content>
        </Layout>
      </Layout>
      {/* پرتال مشتری نه چت دارد نه تایم‌ترکینگ؛ اینها ابزار کار تیم داخلی‌اند. */}
      {!portalCustomer && hasModule("chat") && <FloatChat />}
      <InstallAppPrompt />
      {isMobile && <BottomNavigation />}
    </>
  );
};

const Layout_ = ({ children }) => (
  <SecondaryNavProvider>
    <LayoutPage>{children}</LayoutPage>
  </SecondaryNavProvider>
);

export default Layout_;
